import {
  useFilterStore,
  useMapStore,
  usePointStore,
  usePolygonStore,
} from '@monopolio-ui/state-library'
import { EHeatMapCategory, ESearchLevel, IPolygonProperties } from '@monopolio-ui/types-library'
import { useEffect, useMemo, useRef } from 'react'
import {
  animateChangeHeatMapByCategories,
  animateHoverPolygon,
  filterPolygonByUrlCode,
  heatMapCaseExpressionColors,
  heatMapColors,
  presenterPolygonProperties,
  tileZoomConfiguration,
} from '../../utils/heatMapUtils'
import PolygonPopup from './polygonPopup'
import { usePrevious } from '@wiggot/hooks/src/usePrevious'

const NeighborhoodManager = (): JSX.Element => {
  const {
    map,
    mapZoom,
    isFlyTo,
    isLoadedMap,
    drawingTool,
    hoveredLayer,
    setIsFlyTo,
  } = useMapStore()

  const { searchLevel, setLatitude, setLongitude, setSearchLevel } =
    useFilterStore()

  const {
    polygonTiles,
    heatMapCategory,
    isEnabledHeatMap,
    polygonNavigation,
    setPolygonNavigation,
    clearPolygonNavigation,
  } = usePolygonStore()

  const { pointsSvgContainer } = usePointStore()

  const heatMapCategoryPrevious = usePrevious<EHeatMapCategory | null>(
    heatMapCategory
  )

  const mapZoomPrevious = usePrevious<number>(mapZoom)

  const pointsSvgContainerRef = useRef<any>(pointsSvgContainer)
  pointsSvgContainerRef.current = pointsSvgContainer

  const drawingToolRef = useRef<typeof drawingTool>(drawingTool)
  drawingToolRef.current = drawingTool

  const hoveredLayerRef = useRef<typeof hoveredLayer>(hoveredLayer)
  hoveredLayerRef.current = hoveredLayer

  const heatMapCategoryRef = useRef<EHeatMapCategory | null>(heatMapCategory)
  heatMapCategoryRef.current = heatMapCategory

  const neighborhoodTiles = useMemo(() => {
    const filter = polygonTiles.filter((tile) => tile?.tiles?.neighborhoods?.id)
    return filter.map((tile) => tile.tiles.neighborhoods)
  }, [polygonTiles])

  const loadNeighborhoodLevel = (params: {
    category: EHeatMapCategory
  }): void => {
    if (!map) {
      return
    }

    const { category } = params

    for (const { id } of neighborhoodTiles) {
      const layerName = `${id}-neighborhood-polygon`
      const source = `${id}-neighborhood-source`
      const sourceLayer = id

      const find = map?.getSource(source)

      if (find) {
        continue
      }

      const { minZoom, maxZoom } = tileZoomConfiguration.neighborhood

      map?.addSource(source, {
        type: 'vector',
        url: `mapbox://davpach.${id}`,
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: layerName,
        type: 'fill',
        source,
        'source-layer': sourceLayer,
        filter:
          searchLevel === ESearchLevel.MUNICIPALITY
            ? ['==', 'id', 0]
            : ['!=', 'id', 0],
        paint: {
          'fill-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_color'],
            heatMapCaseExpressionColors[category],
          ],
          'fill-opacity': 0.4,
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      map?.addLayer({
        id: `${id}-neighborhood-border`,
        type: 'line',
        source,
        'source-layer': sourceLayer,
        filter:
          searchLevel === ESearchLevel.MUNICIPALITY
            ? ['==', 'id', 0]
            : ['!=', 'id', 0],
        paint: {
          'line-color': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            heatMapColors[category].hover,
            '#0368FE',
          ],
          'line-width': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            ['feature-state', 'hover_width'],
            1.678,
          ],
        },
        minzoom: minZoom,
        maxzoom: maxZoom,
      })

      createHoverEvent({
        layerName,
        source,
        sourceLayer,
      })
      createClickEvent({ layerName })
    }
  }

  const createHoverEvent = (params: {
    layerName: string
    source: string
    sourceLayer: string
  }) => {
    const { layerName, source, sourceLayer } = params

    let featureId: number | undefined

    map?.on('mousemove', layerName, ({ features }) => {
      if (!features?.length) {
        return
      }

      const feature = features[0]

      if (!feature) {
        return
      }

      if (featureId !== undefined && feature.id !== featureId) {
        if (
          !featureId ||
          (drawingToolRef.current.isActive &&
            drawingToolRef.current.status !== 'processed') ||
          hoveredLayerRef?.current?.layerId
        ) {
          return
        }

        map?.setFeatureState(
          {
            source,
            sourceLayer,
            id: featureId,
          },
          { hover: false }
        )
        featureId = undefined
        return
      }

      const { id } = feature

      if (featureId && featureId === id) {
        return
      }

      featureId = id as number

      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      setTimeout(() => {
        pointsSvgContainerRef?.current?.style('cursor', 'pointer')
        map.getCanvas().style.cursor = 'pointer'
      }, 50)

      let indexKey = 'price_index_rank'
      if (heatMapCategoryRef?.current === EHeatMapCategory.RENT) {
        indexKey = 'rent_index_rank'
      } else if (heatMapCategoryRef?.current === EHeatMapCategory.CAPRATE) {
        indexKey = 'caprate_index_rank'
      }

      const codeLevel = feature?.properties?.[indexKey]
      const heatMapColor = (heatMapCategoryRef?.current ?? heatMapCategory)as keyof (typeof heatMapColors)

      const bgColor =
        heatMapColors[heatMapColor][
          codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
        ] ?? '#ffffff'

      animateHoverPolygon({
        source,
        sourceLayer,
        featureId,
        previousBorder: 1.678,
        nextBorder: 4,
        bgColor,
        bgHoverColor:
          heatMapColors[heatMapColor].hover,
        duration: 300,
        map,
      })

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        {
          hover: true,
          hover_color:
            heatMapColors[heatMapColor][
              codeLevel as keyof (typeof heatMapColors)[EHeatMapCategory]
            ],
          hover_width: 1.678,
        }
      )
    })

    map?.on('mouseleave', layerName, () => {
      if (
        !featureId ||
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed')
      ) {
        return
      }

      pointsSvgContainerRef?.current?.style('cursor', 'grab')
      map.getCanvas().style.cursor = 'grab'

      map?.setFeatureState(
        {
          source,
          sourceLayer,
          id: featureId,
        },
        { hover: false }
      )

      featureId = undefined
    })
  }

  const createClickEvent = (params: { layerName: string }) => {
    const { layerName } = params

    map?.on('click', layerName, (event) => {
      if (
        (drawingToolRef.current.isActive &&
          drawingToolRef.current.status !== 'processed') ||
        hoveredLayerRef?.current?.layerId
      ) {
        return
      }

      map?.flyTo({
        center: event.lngLat,
        essential: true,
        speed: 1.4,
        curve: 1.3,
        duration: 1000,
        zoom: tileZoomConfiguration.neighborhood.maxZoom + 0.1,
      })

      setIsFlyTo(true)

      setTimeout(() => {
        setIsFlyTo(false)
      }, 1000)

      if (!event?.features?.length) {
        return
      }

      clearPolygonNavigation({
        neighborhood: true,
      })

      const properties = presenterPolygonProperties({
        properties: event?.features[0]?.properties,
      })

      setPolygonNavigation({
        level: ESearchLevel.NEIGHBORHOOD,
        properties,
      })

      setSearchLevel(ESearchLevel.NEIGHBORHOOD)
      setLatitude(properties?.centroid?.lat ?? 0)
      setLongitude(properties?.centroid?.lng ?? 0)
    })
  }

  const removeNeighborhoodLevel = (): void => {
    if (!map) {
      return
    }

    for (const { id } of neighborhoodTiles) {
      const find = map?.getSource(`${id}-neighborhood-source`)

      if (!find) {
        continue
      }

      const layerName = `${id}-neighborhood-polygon`
      const source = `${id}-neighborhood-source`

      map.removeLayer(layerName)
      map.removeLayer(`${id}-neighborhood-border`)
      map.removeSource(source)
    }
  }

  const changeNeighborhoodLevelHeatMapColor = (params: {
    category: EHeatMapCategory
    previousCategory: EHeatMapCategory | null
  }): void => {
    if (!map) {
      return
    }

    const { category, previousCategory } = params

    for (const { id } of neighborhoodTiles) {
      const find = map?.getSource(`${id}-neighborhood-source`)

      if (!find) {
        continue
      }

      animateChangeHeatMapByCategories({
        layerName: `${id}-neighborhood-polygon`,
        previousCategory: previousCategory || category,
        nextCategory: category,
        duration: 300,
        map,
      })

      map.setPaintProperty(`${id}-neighborhood-border`, 'line-color', [
        'case',
        ['boolean', ['feature-state', 'hover'], false],
        heatMapColors[category].hover,
        '#0368FE',
      ])
    }
  }
  type TPolygon = {
    state: IPolygonProperties
    municipality: IPolygonProperties
  }
  const filterNeighborhoodPolygonsByParent = (): void => {
    if (!map || !isLoadedMap || !polygonTiles?.length || !isEnabledHeatMap) {
      return
    }

    const { state, municipality } = polygonNavigation as TPolygon

    if (!state && !municipality) {
      resetFilterNeighborhoodPolygons()
      return
    }

    const properties = municipality || state

    for (const { id } of neighborhoodTiles) {
      if (!id) {
        continue
      }

      filterPolygonByUrlCode({
        map,
        layers: [`${id}-neighborhood-polygon`, `${id}-neighborhood-border`],
        urlCode: properties?.fullUrlCode ?? '',
      })
    }
  }

  const resetFilterNeighborhoodPolygons = (): void => {
    if (!map || !polygonTiles?.length || !isLoadedMap || !isEnabledHeatMap) {
      return
    }

    for (const { id } of neighborhoodTiles) {
      const mapLayer = map.getLayer(`${id}-neighborhood-polygon`)
      if (!id || !mapLayer) {
        continue
      }
      map.setFilter(`${id}-neighborhood-polygon`, null)
      map.setFilter(`${id}-neighborhood-border`, null)
    }
  }

  useEffect(() => {
    if (map && isLoadedMap && polygonTiles.length) {
      if (isEnabledHeatMap) {
        loadNeighborhoodLevel({ category: heatMapCategory })
      } else {
        removeNeighborhoodLevel()
      }
    }
  }, [map, isLoadedMap, polygonTiles, isEnabledHeatMap])

  useEffect(() => {
    if (heatMapCategory && isEnabledHeatMap && map) {
      changeNeighborhoodLevelHeatMapColor({
        category: heatMapCategory,
        previousCategory: heatMapCategoryPrevious,
      })
    }
  }, [heatMapCategory])

  useEffect(filterNeighborhoodPolygonsByParent, [
    (polygonNavigation as TPolygon)?.state,
    (polygonNavigation as TPolygon)?.municipality,
    map,
    isLoadedMap,
    polygonTiles,
    isEnabledHeatMap,
  ])

  useEffect(() => {
    if (!map || !isLoadedMap || !polygonTiles?.length) {
      return
    }

    if (
      !mapZoomPrevious ||
      mapZoomPrevious < mapZoom ||
      mapZoom > tileZoomConfiguration.neighborhood.minZoom ||
      tileZoomConfiguration.neighborhood.minZoom - mapZoom < 0.5 ||
      isFlyTo
    ) {
      return
    }

    setPolygonNavigation({
      level: ESearchLevel.MUNICIPALITY,
      properties: undefined,
    })
    setPolygonNavigation({
      level: ESearchLevel.NEIGHBORHOOD,
      properties: undefined,
    })
    setPolygonNavigation({
      level: ESearchLevel.BlOCK,
      properties: undefined,
    })
  }, [mapZoom])

  return (
    <>
      {neighborhoodTiles.map((state) => (
        <PolygonPopup
          key={state.id}
          layer={`${state.id}-neighborhood-polygon`}
          level="neighborhood"
        />
      ))}
    </>
  )
}

export default NeighborhoodManager
